/*
 *  Floating navigation bar that appears when the inline top bar scrolls out of view.
 *  Uses IntersectionObserver to detect visibility of a sentinel element.
 *
 *  Usage:
 *    <FloatingNavBar sentinelRef={this.topBarRef}>
 *      <button>Back</button>
 *      <button>Next</button>
 *    </FloatingNavBar>
 *
 *  The parent must attach sentinelRef to the inline top bar element via ref={sentinelRef}.
 */
import React from 'react';
import { decorate, observable, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';

class FloatingNavBar extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.shouldShow = false;
      this.mounted = false;
    });
    this.observer = null;
    this.hideTimer = null;
  }

  componentDidMount() {
    const sentinel = this.props.sentinelRef && this.props.sentinelRef.current;
    if (sentinel && typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const isVisible = !entry.isIntersecting;
          if (isVisible) {
            if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
            runInAction(() => { this.mounted = true; });
            // Trigger enter animation on next frame
            requestAnimationFrame(() => { runInAction(() => { this.shouldShow = true; }); });
          } else {
            // Start exit animation, then unmount
            runInAction(() => { this.shouldShow = false; });
            this.hideTimer = setTimeout(() => {
              runInAction(() => { this.mounted = false; });
            }, 400);
          }
        },
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' },
      );
      this.observer.observe(sentinel);
    }
  }

  componentWillUnmount() {
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
  }

  render() {
    if (!this.mounted) return null;

    return (
      <div className={`floating-nav-bar ${this.shouldShow ? 'entering' : 'exiting'}`}>
        <div className="floating-nav-bar-inner">
          {this.props.children}
        </div>
      </div>
    );
  }
}

decorate(FloatingNavBar, {
  shouldShow: observable,
  mounted: observable,
});

export default observer(FloatingNavBar);
